import { Button, Paper, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { format } from 'date-fns';
import { memo, ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { ReactComponent as CheckIcon } from '~/ui/assets/images/aiAssist/check-green.svg';
import { ReactComponent as RewriteIcon } from '~/ui/assets/images/aiAssist/refresh-square.svg';
import { ReactComponent as RevertIcon } from '~/ui/assets/images/aiAssist/revert.svg';
import Textarea from '~/ui/components/inputs/Textarea';
import { ILoadingProps } from '../../types';

import styles from '../../AiAssist.module.scss';

const AIAssistedNote = ({ setLoading, loading }: ILoadingProps): ReactElement => {
  const { noteAssist } = useStoreState(state => state.aiAssist);
  const { onGetAIAssist, setNoteAssist } = useStoreActions(actions => actions.aiAssist);
  const { current } = useStoreState(state => state.user);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const [assistedNote, originalNote] = watch(['assistedNote', 'originalNote']);

  useEffect(() => {
    if (noteAssist) {
      setValue('assistedNote', noteAssist);
    }
  }, [noteAssist, setValue]);

  useEffect(
    () => () => {
      setNoteAssist('');
      setValue('assistedNote', '');
    },
    [setNoteAssist, setValue],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(assistedNote);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = assistedNote;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleOriginalNote = () => {
    setValue('assistedNote', originalNote);
  };

  const handleAccept = () => {
    setValue('assistedNote', assistedNote);
    setValue('note', assistedNote);
  };

  const handleRewrite = async () => {
    setLoading(true);
    await onGetAIAssist({
      clinicId: current.clinic.id,
      data: { note: originalNote, date: format(new Date(), 'yyyy-MM-dd') },
    });
    setLoading(false);
  };

  return (
    <Paper className={styles.paper}>
      <Typography variant="h6" className={styles.title}>
        AI Assisted Note
      </Typography>

      <Textarea
        register={register}
        errors={errors}
        rows={18}
        name="assistedNote"
        showCopyButton={!!assistedNote}
        onCopy={handleCopy}
        readOnly
      />

      <div className={styles.aiActions}>
        <Button
          variant="text"
          className={classNames(styles.original, { [styles.disabled]: !assistedNote })}
          disabled={!assistedNote || !originalNote || loading}
          endIcon={<RevertIcon />}
          onClick={handleOriginalNote}
        >
          Original
        </Button>
        <Button
          variant="text"
          className={classNames(styles.rewrite, { [styles.disabled]: !assistedNote })}
          disabled={!assistedNote || loading}
          endIcon={<RewriteIcon />}
          onClick={handleRewrite}
        >
          Rewrite
        </Button>
        <Button
          variant="text"
          className={classNames(styles.accept, { [styles.disabled]: !assistedNote })}
          disabled={!assistedNote || loading}
          endIcon={<CheckIcon />}
          onClick={handleAccept}
        >
          Accept
        </Button>
      </div>
    </Paper>
  );
};

export default memo(AIAssistedNote);
